import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Scissors, Chair, Calendar, Sparkle } from "@phosphor-icons/react";
import Navigation from "../components/Navigation";
import Footer from "../components/Footer";
import { useSeo } from "../hooks/useSeo";
import FloatingAppButton from "../components/FloatingAppButton";
import WhatsAppButton from "../components/WhatsAppButton";
import InstagramSection from "../components/InstagramSection";
import { ReviewsSection } from "../components/ReviewsSection";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_f92832a4-f773-4854-853a-afbb4cec93dc/artifacts/hfvgmn1m_JS%20Blond%20Logo%20white.png";

const highlights = [
  {
    icon: Scissors,
    title: "Blond & Balayage",
    description: "Balayage, AirTouch, Haarverlängerung & mehr in Heilbronn.",
    link: "/services",
    cta: "Preise ansehen",
    image: "https://customer-assets.emergentagent.com/job_1a92d488-6b73-421e-b552-cb97ed3d4201/artifacts/ux144oee_IMG_8663.jpeg",
  },
  {
    icon: Chair,
    title: "Co-Working",
    description: "Selbstständig arbeiten ohne eigenen Salon.",
    link: "/coworking",
    cta: "Mehr erfahren",
    image: "https://customer-assets.emergentagent.com/job_1a92d488-6b73-421e-b552-cb97ed3d4201/artifacts/arzrg92d_IMG_8664.jpeg",
  },
  {
    icon: Calendar,
    title: "Education",
    description: "Masterclasses, Seminare & Community für Friseure.",
    link: "/education",
    cta: "Zum Education Hub",
    image: "https://customer-assets.emergentagent.com/job_1a92d488-6b73-421e-b552-cb97ed3d4201/artifacts/j46szm81_IMG_8661.jpeg",
  },
];

const HomePage = () => {
  useSeo({
    title: "JS Blond Salon Heilbronn | Blond Spezialist, Balayage, AirTouch & Haarverlängerung",
    description: "Blond Spezialist in Heilbronn/Leingarten: Balayage, AirTouch, Haarverlängerung & Premium-Blondierungen von Friseurmeisterin Jennifer Vallentin. Jetzt online Termin buchen.",
    path: "/",
  });


  return (
    <div className="min-h-screen bg-[#F9F7F2]">
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative h-screen w-full overflow-hidden">
        <div className="absolute inset-0">
          <motion.img
            initial={{ scale: 1.1, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 1.5, ease: "easeOut" }}
            src="https://static.wixstatic.com/media/5071cb_20261aee77c64b48a57ada51485234e4~mv2.jpeg/v1/fill/w_1904,h_992,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/5071cb_20261aee77c64b48a57ada51485234e4~mv2.jpeg"
            alt="JS Blond Salon Heilbronn - Blond Spezialist, Balayage, AirTouch und Haarverlängerung"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
        </div>

        <div className="relative z-10 h-full flex flex-col items-center justify-center text-center container-padding">
          <motion.img
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.8 }}
            src={LOGO_URL}
            alt="JS Blond Salon"
            className="h-20 md:h-28 w-auto mb-6"
          />

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.8 }}
            className="mt-4 max-w-3xl"
          >
            <h1
              data-testid="home-h1"
              className="text-2xl sm:text-3xl md:text-4xl font-normal text-white leading-tight tracking-tight"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Blond Spezialist in Heilbronn – Balayage, AirTouch & Haarverlängerung
            </h1>
            <p className="text-base md:text-lg font-light text-white/85 mt-4">
              Premium Ergebnisse von unserem spezialisierten Team in Leingarten.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.8 }}
            className="flex flex-col sm:flex-row gap-4 mt-10"
          >
            <motion.a
              href="https://www.phorest.com/salon/jsblondsalonughaftungsbeschrae"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-book-btn"
              className="bg-white text-[#2C2C2C] px-10 py-5 rounded-full uppercase tracking-widest text-xs font-bold hover:bg-[#D4AF37] hover:text-white transition-all duration-300"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
            >
              Termin Buchen
            </motion.a>
            <Link to="/services">
              <motion.div
                data-testid="hero-services-btn"
                className="border border-white text-white px-10 py-5 rounded-full uppercase tracking-widest text-xs font-bold hover:bg-white hover:text-[#2C2C2C] transition-all duration-300 cursor-pointer"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
              >
                Services & Preise
              </motion.div>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Highlight Cards */}
      <section className="section-spacing bg-white">
        <div className="container-padding">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <span className="caption block mb-4">Entdecke JS</span>
            <h2
              className="text-4xl md:text-5xl font-normal tracking-tight"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Was wir bieten
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {highlights.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Link to={item.link} className="block h-full">
                  <div 
                    className="bg-white h-full flex flex-col border border-transparent hover:border-[#9A8C73] transition-all duration-300 group overflow-hidden"
                    data-testid={`highlight-card-${index}`}
                  >
                    <div className="h-48 overflow-hidden">
                      <img 
                        src={item.image} 
                        alt={item.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-8 flex flex-col flex-grow">
                      <item.icon size={32} className="text-[#D4AF37] mb-4" weight="light" />
                      <h3
                        className="text-2xl font-medium mb-3"
                        style={{ fontFamily: "'Playfair Display', serif" }}
                      >
                        {item.title}
                      </h3>
                      <p className="text-[#2C2C2C]/70 font-light flex-grow mb-6">
                        {item.description}
                      </p>
                      <div className="flex items-center gap-2 text-[#9A8C73] group-hover:text-[#2C2C2C] transition-colors">
                        <span className="text-sm uppercase tracking-widest font-medium">{item.cta}</span>
                        <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why JS Teaser */}
      <section className="section-spacing bg-[#F9F7F2]">
        <div className="container-padding">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <img
                src="https://customer-assets.emergentagent.com/job_1a92d488-6b73-421e-b552-cb97ed3d4201/artifacts/fc0pl5ex_IMG_8659.jpeg"
                alt="JS Blond Salon Empfang – salon | coworking | events"
                className="w-full h-[400px] lg:h-[500px] object-cover"
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <span className="caption block mb-4">Warum JS?</span>
              <h2
                className="text-3xl md:text-4xl font-normal tracking-tight mb-6"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Weil Blond kein Zufall ist
              </h2>
              <p className="text-[#2C2C2C]/70 font-light leading-relaxed mb-6">
                Erfahrung, Präzision und Haltung. Bei uns geht es nicht um schnelle Trends 
                oder „Hauptsache hell". Wir stehen für durchdachte Blondkonzepte, ehrliche 
                Beratung und Ergebnisse, die langfristig tragbar sind.
              </p>
              <div className="flex flex-wrap gap-4">
                <div className="flex items-center gap-2">
                  <Sparkle size={20} className="text-[#D4AF37]" weight="fill" />
                  <span className="text-sm">Ruhe statt Hektik</span>
                </div>
                <div className="flex items-center gap-2">
                  <Sparkle size={20} className="text-[#D4AF37]" weight="fill" />
                  <span className="text-sm">Qualität statt Masse</span>
                </div>
                <div className="flex items-center gap-2">
                  <Sparkle size={20} className="text-[#D4AF37]" weight="fill" />
                  <span className="text-sm">Ehrliche Beratung</span>
                </div>
              </div>
              
              <motion.p
                className="mt-8 text-lg italic text-[#9A8C73]"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                "Kund:innen wählen uns, weil sie Vertrauen wollen. 
                Und bleiben, weil sie merken, dass es berechtigt ist."
              </motion.p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Google Reviews / Social Proof */}
      <ReviewsSection />

      {/* Instagram Section */}
      <InstagramSection />

      {/* CTA Section */}
      <section className="py-20 bg-[#2C2C2C] text-white">
        <div className="container-padding text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2
              className="text-3xl md:text-4xl font-normal tracking-tight mb-6"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Bereit für dein neues Blond?
            </h2>
            <p className="text-white/70 font-light mb-8 max-w-xl mx-auto">
              Termine können 24h online gebucht werden. Wir freuen uns auf dich!
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.a
                href="https://www.phorest.com/salon/jsblondsalonughaftungsbeschrae"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="cta-book-btn"
                className="bg-[#D4AF37] text-[#2C2C2C] px-10 py-5 rounded-full uppercase tracking-widest text-xs font-bold hover:bg-white transition-colors"
                whileHover={{ scale: 1.05 }}
              >
                Termin Buchen
              </motion.a>
              <Link to="/kontakt">
                <motion.div
                  data-testid="cta-contact-btn"
                  className="border border-white text-white px-10 py-5 rounded-full uppercase tracking-widest text-xs font-bold hover:bg-white hover:text-[#2C2C2C] transition-colors cursor-pointer"
                  whileHover={{ scale: 1.05 }}
                >
                  Kontakt
                </motion.div>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
      <FloatingAppButton />
      <WhatsAppButton />
    </div>
  );
};

export default HomePage;
